/**
 * Client wrappers for the server-side script library. Producers sync their own
 * scripts; the super-admin browses/edits everyone's.
 */
import type { Project } from '@goanna-im/shared';

export interface ScriptSummary {
  id: string;
  ownerId: string;
  ownerName: string;
  title: string;
  updatedAt: number;
  blocks: number;
}
export interface StoredScript extends ScriptSummary {
  project: Project;
}

async function api<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body ? { 'content-type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error ?? 'Request failed');
  return data as T;
}

// --- Producer (own scripts) ---
export async function listMyScripts(): Promise<StoredScript[]> {
  return (await api<{ scripts: StoredScript[] }>('/api/library')).scripts;
}
export async function saveMyScript(project: Project): Promise<StoredScript> {
  return (await api<{ script: StoredScript }>(`/api/library/${encodeURIComponent(project.id)}`, 'PUT', { project })).script;
}
export async function deleteMyScript(id: string): Promise<void> {
  await api(`/api/library/${encodeURIComponent(id)}`, 'DELETE');
}

// --- Admin (all scripts) ---
export async function listAllScripts(): Promise<ScriptSummary[]> {
  return (await api<{ scripts: ScriptSummary[] }>('/api/admin/library')).scripts;
}
export async function getScript(id: string): Promise<StoredScript> {
  return (await api<{ script: StoredScript }>(`/api/admin/library/${encodeURIComponent(id)}`)).script;
}
export async function adminSaveScript(project: Project): Promise<StoredScript> {
  return (await api<{ script: StoredScript }>(`/api/admin/library/${encodeURIComponent(project.id)}`, 'PUT', { project })).script;
}
export async function adminDeleteScript(id: string): Promise<void> {
  await api(`/api/library/${encodeURIComponent(id)}`, 'DELETE');
}
